(function () {
  if (customElements.get('video-comparison-slider')) return;

  const MIN = 25;
  const MAX = 75;
  const KEY_STEP = 5;
  const DAYS = 14;
  const DEFAULT_LEFT = 'https://hey-silo.b-cdn.net/strawberry_wwebbsite_red3.webm';
  const DEFAULT_RIGHT = 'https://hey-silo.b-cdn.net/strawberry_wwebbsite_black3.webm';

  class VideoComparisonSlider extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('#slider');
      this.handle = this.slider?.querySelector('.handle');
      this.leftVideo = this.querySelector('#video-left');
      this.rightVideo = this.querySelector('#video-right');
      if (!this.slider || !this.handle || !this.leftVideo || !this.rightVideo) return;

      // The frame (.video-wrapper) is the geometry reference: the host carries side padding.
      this.frame = this.querySelector('.video-wrapper') || this;
      this.textLeft = this.querySelector('.text-left');
      this.textRight = this.querySelector('.text-right');
      this.dayCounter = this.querySelector('#day-counter');
      this.replayBtn = this.querySelector('[data-comparison-replay]');

      this.sources = {
        left: this.dataset.srcLeft || DEFAULT_LEFT,
        right: this.dataset.srcRight || DEFAULT_RIGHT,
      };

      this.dragging = false;
      this.moved = false;
      this.rect = null;
      this.raf = 0;
      this.pendingX = 0;
      this.percent = 50;
      this.day = null; // last day written to the counter
      this.loaded = false;

      this._ac = new AbortController();
      const { signal } = this._ac;

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      // Markup without the t-touch-none utility (the live section) still needs the handle to own touch moves.
      if (!this.handle.classList.contains('t-touch-none')) this.handle.style.touchAction = 'none';
      if (!this.handle.hasAttribute('role')) this.handle.setAttribute('role', 'slider');
      if (!this.handle.hasAttribute('aria-valuemin')) this.handle.setAttribute('aria-valuemin', String(MIN));
      if (!this.handle.hasAttribute('aria-valuemax')) this.handle.setAttribute('aria-valuemax', String(MAX));
      this.handle.setAttribute('aria-valuenow', String(this.percent));

      this.handle.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
      this.handle.addEventListener('keydown', (e) => this.onKeydown(e), { signal });
      this.frame.addEventListener('click', (e) => this.onClick(e), { signal });
      this.replayBtn?.addEventListener('click', (e) => this.onReplay(e), { signal });
      this.leftVideo.addEventListener('timeupdate', () => this.onTimeUpdate(), { signal });
      this.leftVideo.addEventListener('ended', () => this.onEnded(), { signal });

      // Lazy-load the videos only once the slider is actually on screen.
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.loadVideos();
            this.io.disconnect();
            this.io = null;
          }
        },
        { rootMargin: '200px' },
      );
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.io = null;
      this._ac?.abort();
      this._ac = null;
      this.onPointerUp();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      // Release the media so a re-connect (editor re-render) loads clean.
      for (const video of [this.leftVideo, this.rightVideo]) {
        if (!video) continue;
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      this.loaded = false;
    }

    /* ------------------------------ playback ------------------------------ */

    loadVideos() {
      if (this.loaded) return;
      this.loaded = true;

      this.leftVideo.src = this.sources.left;
      this.rightVideo.src = this.sources.right;
      this.leftVideo.load();
      this.rightVideo.load();
      this.play(this.leftVideo);
      this.play(this.rightVideo);
    }

    play(video) {
      const result = video.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }

    // Writes the counter only when the day actually changes (timeupdate fires ~4x/s).
    setDay(day) {
      if (!this.dayCounter || day === this.day) return;
      this.day = day;
      this.dayCounter.textContent = `Day: ${day}`;
    }

    onTimeUpdate() {
      const d = this.leftVideo.duration;
      if (!Number.isFinite(d) || d <= 0) return;
      const day = Math.round((Math.min(this.leftVideo.currentTime, d) / d) * DAYS);
      this.setDay(Number.isFinite(day) ? day : 0);
    }

    onEnded() {
      this.setDay(DAYS);
    }

    onReplay(e) {
      // Don't let the click fall through to the side-snap handler on the frame.
      e.stopPropagation();
      if (!this.loaded) {
        this.loadVideos();
        return;
      }
      this.leftVideo.currentTime = 0;
      this.rightVideo.currentTime = 0;
      this.play(this.leftVideo);
      this.play(this.rightVideo);
      this.day = null;
      this.setDay(0);
    }

    /* ------------------------------- drag -------------------------------- */

    onPointerDown(e) {
      e.preventDefault();
      this.dragging = true;
      this.moved = false;
      this.rect = this.frame.getBoundingClientRect(); // cached once per drag
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(e) {
      if (!this.dragging) return;
      this.moved = true;
      this.pendingX = e.clientX;
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        const rect = this.rect || this.frame.getBoundingClientRect();
        this.applyPercent(((this.pendingX - rect.left) / rect.width) * 100);
      });
    }

    onPointerUp() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onKeydown(e) {
      let next = null;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = this.percent - KEY_STEP;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next = this.percent + KEY_STEP;
          break;
        case 'Home':
          next = MIN;
          break;
        case 'End':
          next = MAX;
          break;
        default:
          return;
      }
      e.preventDefault();
      this.applyPercent(next);
    }

    onClick(e) {
      // Ignore the click that fires at the end of a drag.
      if (this.moved) {
        this.moved = false;
        return;
      }
      const rect = this.frame.getBoundingClientRect();
      const percent = ((e.clientX - rect.left) / rect.width) * 100;
      // Click a side → reveal the opposite (preserves the original snap behaviour).
      this.applyPercent(percent < 50 ? 70 : 30);
    }

    /* ----------------------------- rendering ----------------------------- */

    applyPercent(pct) {
      const clamped = Math.max(MIN, Math.min(pct, MAX));
      this.slider.style.left = `${clamped}%`;
      this.rightVideo.style.clipPath = `inset(0 ${100 - clamped}% 0 0)`;

      if (clamped === this.percent) return;
      this.percent = clamped;
      this.handle.setAttribute('aria-valuenow', String(Math.round(clamped)));

      if (!this.textLeft || !this.textRight) return;
      if (clamped < 40) {
        this.textLeft.classList.add('fade');
        this.textLeft.classList.remove('center');
        this.textRight.classList.remove('fade');
        this.textRight.classList.add('center');
      } else if (clamped > 60) {
        this.textRight.classList.add('fade');
        this.textRight.classList.remove('center');
        this.textLeft.classList.remove('fade');
        this.textLeft.classList.add('center');
      } else {
        this.textLeft.classList.remove('fade', 'center');
        this.textRight.classList.remove('fade', 'center');
      }
    }
  }

  customElements.define('video-comparison-slider', VideoComparisonSlider);
})();
